import React, { FC, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Step, StepLabel, Stepper, CircularProgress } from '@material-ui/core'
import ActionData from '../../../types/ActionData'
import GeneralInfo from '../../ApplicantDashboard/Application/GeneralInfo'
import ContactInfo from '../../ApplicantDashboard/Application/ContactInfo'
import OrganizationalInfo from '../../ApplicantDashboard/Application/OrganizationalInfo'
import DisclosureQuestions from '../../ApplicantDashboard/Application/DisclosureQuestions'

import { mainColors } from '../../../assets/colors'
import './index.css'

interface Props {
  show: boolean;
  applicationType: string;
  updatingProviderApplicationForm: boolean;
  generalInfoState: any;
  updateGeneralInfo: ActionData;
  contactInfoState: any;
  updateContactInfo: ActionData;
  organizationalInfoState: any;
  updateOrgInfo: ActionData;
  disclosureQuestionsState: any;
  updateDisclosureQuestions: ActionData;
  readonly: boolean;
  onChangeGeneralInfoState: (value: any) => void;
  onChangeContactInfoState: (value: any) => void;
  onChangeOrganizationalInfoState: (value: any) => void;
  onChangeDisclosureQuestionsState: (value: any) => void;
  onClose: () => void;
  onSubmit: (value: any, step: number, submitItem: boolean) => void;
}

const getSteps = () => {
  return ['General Info', 'Contact Info', 'Organization Info', 'Disclosure Questions']
}

const getStepContent = (
  stepIndex: number, 
  applicationType: string, 
  generalInfoState: any, 
  contactInfoState,
  organizationalInfoState,
  disclosureQuestionsState,
  readonly,
  onSubmit: (values: any, step: number, submitItem: boolean) => void,
  handleNext: (values: any) => void,
  handleBack: (values: any) => void
) => {
  switch(stepIndex) {
    case 0:
      return ( 
        <GeneralInfo
          activeStep={stepIndex}
          applicationType={applicationType}
          initialValues={generalInfoState}
          readonly={readonly}
          handleNext={handleNext}
          onSubmit={(values: any) => onSubmit(values, stepIndex, false)}
        />
      )
    case 1:
      return (
        <ContactInfo 
          initialValues={contactInfoState}
          readonly={readonly}
          handleNext={handleNext} 
          handleBack={handleBack}
          onSubmit={(values: any) => onSubmit(values, stepIndex, false)}
        />
      )
    case 2: 
      return (
        <OrganizationalInfo
          initialValues={organizationalInfoState}
          readonly={readonly}
          handleNext={handleNext}
          handleBack={handleBack}
          onSubmit={(values: any) => onSubmit(values, stepIndex, false)}
        />
      )
    case 3:
      return (
        <DisclosureQuestions 
          initialValues={disclosureQuestionsState}
          readonly={readonly}
          handleBack={handleBack}
          onSubmit={(values: any, submitItem: boolean) => onSubmit(values, stepIndex, submitItem)}
        />
      )
    default:
      return 'Unknown Step'
  }
}

const ProviderApplicationModal: FC<Props> = 
  ({ 
    show,
    applicationType,
    updatingProviderApplicationForm,
    generalInfoState,
    updateGeneralInfo,
    contactInfoState,
    updateContactInfo,
    organizationalInfoState,
    updateOrgInfo,
    disclosureQuestionsState,
    updateDisclosureQuestions,
    readonly,
    onChangeGeneralInfoState, 
    onChangeContactInfoState, 
    onChangeOrganizationalInfoState,
    onChangeDisclosureQuestionsState,
    onClose,
    onSubmit
  }: Props) => {
    const [activeStep, setActiveStep] = useState(0)
    const steps = getSteps()

    const handleHide = () => {
      setActiveStep(0)
      onClose()
    }

    const handleNext = (values: any) => {
      if (activeStep === 0) {
        onChangeGeneralInfoState(values)
      } else if (activeStep === 1) {
        onChangeContactInfoState(values)
      } else if(activeStep === 2) {
        onChangeOrganizationalInfoState(values)
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = (values: any) => {
      if (activeStep === 1) {
        onChangeContactInfoState(values)
      } else if(activeStep === 2) {
        onChangeOrganizationalInfoState(values)
      } else if(activeStep === 3 ) {
        onChangeDisclosureQuestionsState(values)
      }

      setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const loadingIcon = updateGeneralInfo.error !== false || updateContactInfo.error !== false || updateOrgInfo.error !== false || updateDisclosureQuestions.error !== false ? <FontAwesomeIcon icon={['fas', 'exclamation-circle']} color={mainColors.loadingBlue} className='modal-success-icon' /> : <CircularProgress size={70} />
    const modalText =  updateGeneralInfo.error !== false || updateContactInfo.error !== false || updateOrgInfo.error !== false || updateDisclosureQuestions.error !== false ? 'The data could not be saved.' : 'Updating Provider Application Form'

    return (
      <Modal size='xl' backdrop='static' show={show} onHide={handleHide}>
        <Modal.Header closeButton id='modal-header'>
          <FontAwesomeIcon icon={['fas', 'edit']} size='lg' className='modal-header-icon' />
          <Modal.Title id='modal-header-title'>Provider Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updatingProviderApplicationForm ? ( 
            updateGeneralInfo.loading || updateGeneralInfo.error!== false || updateContactInfo.loading || updateContactInfo.error!== false || updateOrgInfo.loading || updateOrgInfo.error !== false || updateDisclosureQuestions.loading || updateDisclosureQuestions.error !== false ? (
              <div className='modal-loading-container'>
                {loadingIcon}
                <span className='modal-loading-text' style={updateGeneralInfo.error !== false || updateContactInfo.error !== false  || updateOrgInfo.error !== false || updateDisclosureQuestions.error !== false ? {width: '100%'} : {}}>{modalText}</span>
              </div>
            ) : updateGeneralInfo.success || updateContactInfo.success  || updateOrgInfo.success || updateDisclosureQuestions.success !== false ? (
              <div>
                <div className='modal-success-container'>
                  <FontAwesomeIcon icon={['far', 'check-circle']} color={mainColors.successGreen} className='modal-success-icon' />
                  <span className='modal-loading-text'>Success!</span>
                  <span className='modal-success-text'>You have {updateDisclosureQuestions.data.submitItem === 'SUBMITTED' ? 'submitted' : 'updated'} your Provider Application Form.</span>
                </div>
              </div>
            ) : (
              <div></div>
            )
          ) : (
            <div style={{ width: '100%' }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {
                  steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))
                }
              </Stepper>
              {
                getStepContent(
                  activeStep,
                  applicationType,
                  generalInfoState,
                  contactInfoState,
                  organizationalInfoState,
                  disclosureQuestionsState,
                  readonly,
                  onSubmit,
                  handleNext,
                  handleBack
                )
              }
            </div>
          )}
        </Modal.Body>
      </Modal>
    )
  }

export default ProviderApplicationModal